import { useEffect, useState } from "react";

/**
 * MS192: shared helpers for the cross-page hand-off of the active
 * landing-hero chat session.
 *
 * Pre-MS192, a buyer who started a conversation on the landing hero
 * and then clicked a "Send a gift" / "Try Lembra" CTA was bounced
 * into /chat with a fresh, empty conversation — the hero session
 * just vanished. Frustrating because the buyer thought the CTA was
 * a continuation, not a reset.
 *
 * The fix: useInlineConversation mirrors its active sessionId into
 * sessionStorage as it changes (per-tab, never localStorage so
 * conversations don't bleed across tabs). Any link to /chat that
 * wraps `chatHref(...)` reads that value and appends
 * `?sessionId=<id>` — the /chat page already restores from that
 * query param. The handoff is invisible.
 *
 * Cleared by useInlineConversation when a hard reset happens
 * (status: circuit_broken → user clicks "Start a new chat").
 */

const HERO_SESSION_KEY = "lembra:hero_session_id";

/** Read the active hero sessionId (or null) — SSR-safe. */
export function readHeroSessionId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(HERO_SESSION_KEY);
  } catch {
    return null;
  }
}

/**
 * MS192: useHeroSessionId — React hook that subscribes to the
 * stored sessionId so consumers re-render when it changes.
 *
 * Why: the storage WRITE happens inside useInlineConversation
 * (mounted under VariantB → InlineConcierge). Components like
 * Index.tsx that render the "Send a gift" CTA don't share React
 * state with that subtree, so without subscribing they'd never
 * re-render when the sessionId lands and the link would carry
 * a stale URL.
 *
 * We listen for two events:
 *   - "storage" — fired in OTHER tabs when sessionStorage changes
 *     in this tab. Useless for our case (same-tab) but cheap.
 *   - A bespoke "lembra:hero_session_changed" custom event we
 *     dispatch from useInlineConversation when the id updates.
 *     This is what actually makes same-tab subscription work.
 *
 * SSR returns null and re-syncs on hydration (the useEffect
 * runs only on the client).
 */
export const HERO_SESSION_EVENT = "lembra:hero_session_changed";

export function useHeroSessionId(): string | null {
  const [id, setId] = useState<string | null>(() => readHeroSessionId());
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = () => setId(readHeroSessionId());
    window.addEventListener("storage", handler);
    window.addEventListener(HERO_SESSION_EVENT, handler);
    return () => {
      window.removeEventListener("storage", handler);
      window.removeEventListener(HERO_SESSION_EVENT, handler);
    };
  }, []);
  return id;
}

/**
 * Build a /chat href that picks up the active hero conversation
 * when one exists. Pass the base path you'd link to ("/chat" today,
 * future authed surfaces could pass "/dashboard/chat"). If no hero
 * session is active, returns the base path unchanged so the caller's
 * link goes to a fresh /chat.
 *
 * Merges with any extra query params the caller passes (e.g. a
 * `recipient=...` deep-link). The sessionId always wins because
 * restoring beats deep-linking — once a sessionId is restored, the
 * /chat page reads recipient + occasion from the resolved_brief on
 * the chat_sessions row anyway.
 */
export function chatHref(base = "/chat", extra?: Record<string, string>): string {
  const sessionId = readHeroSessionId();
  const params = new URLSearchParams(extra ?? {});
  if (sessionId) params.set("sessionId", sessionId);
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}
